import type { BillingCheckoutInput, BillingDto, BillingPlan, PlanLimits } from '@af/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/ui/brand-logo';
import { Button } from '@/components/ui/button';
import { ErrorBox } from '@/components/ui/misc';
import { activeOrg } from '@/lib/active-org';
import { call } from '@/lib/api';
import { auth } from '@/lib/auth';
import { authClient } from '@/lib/auth/better-auth';
import { planChoice } from '@/lib/plan-choice';
import { useMe } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { PLAN_NAMES, PlanPicker } from './billing-plans';
import { CheckoutSuccess, ConfirmingPayment } from './billing-success';
import { SecureAccountGate } from './secure-account';

/** Checkout confirmation arrives by webhook: poll only right after checkout or while it is pending. */
const returnedFromCheckout = () =>
  typeof window !== 'undefined' && /[?&]checkout=success/.test(window.location.search);

function Meter({
  label,
  used,
  limit,
  locale,
}: {
  label: string;
  used: number;
  limit: number | null;
  locale: string;
}) {
  const { t } = useTranslation();
  const fmt = (n: number) => n.toLocaleString(locale, { maximumFractionDigits: 1 });
  const pct = limit ? Math.min(100, (used / limit) * 100) : 0;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="text-muted">{label}</span>
        <span className="font-mono text-xs">
          {fmt(used)} / {limit === null ? t('billing.unlimited') : fmt(limit)}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={limit ?? undefined}
        aria-valuenow={used}
        className="h-1.5 overflow-hidden rounded-full bg-panel-2"
      >
        <div
          className={cn(
            'h-full rounded-full',
            pct >= 100 ? 'bg-danger' : pct >= 80 ? 'bg-warn' : 'bg-accent',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function BillingPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('nl') ? 'nl-NL' : 'en-GB';
  const eur = (cents: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(cents / 100);
  const me = useMe();
  const status = useQuery({
    queryKey: ['billing', activeOrg.get()],
    queryFn: () => call('billingStatus'),
    enabled: !!me.data?.organization,
    refetchInterval: (q) =>
      !q.state.data?.hasAccess && (returnedFromCheckout() || q.state.data?.status === 'pending')
        ? 3000
        : false,
  });
  const plans = useQuery({ queryKey: ['billing-plans'], queryFn: () => call('billingPlans') });
  const orgs = useQuery({ queryKey: ['billing-orgs'], queryFn: () => auth.listOrganizations() });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // A failed checkout must be seen: the plan cards can be far above the footer.
    if (error) errorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [error]);
  const b = status.data;
  const [suggested] = useState(planChoice);
  const priceText = (p: BillingPlan) =>
    t(plans.data?.taxMode === 'kor' ? 'billing.korVat' : 'billing.exclVat', { price: eur(p.amount) });
  const current = plans.data?.plans.find((p) => p.plan === b?.plan && p.interval === b?.interval);
  const go = async (choice?: BillingCheckoutInput) => {
    setBusy(true);
    setError(null);
    try {
      const { url } = choice ? await call('billingCheckout', { body: choice }) : await call('billingPortal');
      window.location.assign(url);
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  const choosing = !!b?.canManage && ['pending', 'canceled', 'incomplete_expired'].includes(b.status);
  const showStatus =
    !!b &&
    !b.exempt &&
    (b.hasAccess ||
      !!b.plan ||
      !!b.trialEnd ||
      !!b.paidThrough ||
      !!b.hostedInvoiceUrl ||
      b.status !== 'pending');
  // New owners secure the account (TOTP) before paying: the portal and other privileged actions need it anyway.
  if (me.data?.needs2fa && !me.data.impersonation) return <SecureAccountGate />;
  const justPaid = returnedFromCheckout() && !!b && !b.exempt;
  const successView = justPaid && !!b?.hasAccess;
  const confirming = justPaid && !b?.hasAccess && b?.status === 'pending';
  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-6 sm:px-6 sm:py-10">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Logo size={32} />
            <span className="text-lg font-semibold tracking-tight">Mandistry</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label htmlFor="billing-org" className="sr-only">
              {t('billing.switchOrg')}
            </label>
            <select
              id="billing-org"
              className="h-8 rounded-lg border border-border bg-bg px-2 text-sm"
              value={me.data?.organization?.id ?? ''}
              onChange={async (e) => {
                setBusy(true);
                setError(null);
                try {
                  await auth.setActiveOrganization(e.target.value);
                  activeOrg.set(e.target.value);
                  window.location.assign('/billing');
                } catch (e) {
                  setError(e);
                  setBusy(false);
                }
              }}
            >
              {!me.data?.organization && <option value="">{t('billing.chooseOrg')}</option>}
              {orgs.data?.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            <a href="/settings/security" className="px-2 text-muted hover:text-fg">
              {t('billing.security')}
            </a>
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                await auth.signOut();
                window.location.assign('/login');
              }}
            >
              {t('billing.signOut')}
            </Button>
          </div>
        </header>

        <div ref={errorRef} className="mx-auto w-full max-w-3xl empty:hidden">
          <ErrorBox error={error ?? status.error ?? plans.error} />
        </div>

        {successView && b ? (
          <CheckoutSuccess
            b={b}
            price={current}
            taxMode={plans.data?.taxMode ?? 'kor'}
            email={me.data?.user.email ?? ''}
            orgName={me.data?.organization?.name ?? ''}
            locale={locale}
            busy={busy}
            onManage={() => void go()}
          />
        ) : confirming ? (
          <ConfirmingPayment />
        ) : (
          <>
            <div className="flex flex-col items-center gap-2 text-center">
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                {t(choosing ? 'billing.chooseTitle' : 'billing.title')}
              </h1>
              <p className="max-w-xl text-sm text-muted">
                {me.data?.organization?.name}
                {choosing && ` · ${t('billing.chooseSubtitle')}`}
              </p>
            </div>

            {b?.exempt && (
              <p className="rounded-xl border border-border bg-bg p-5 text-center">{t('billing.exempt')}</p>
            )}

            {showStatus && b && (
              <section className="mx-auto flex w-full max-w-3xl flex-col gap-5 rounded-xl border border-border bg-bg p-5">
                <div className="flex flex-col gap-1">
                  {b.hasAccess && <p className="text-sm text-success">{t('billing.hasAccess')}</p>}
                  {b.plan && (
                    <p className="text-lg font-medium">
                      {t('billing.currentPlan', { plan: PLAN_NAMES[b.plan] })}
                      {b.interval && (
                        <>
                          {' · '}
                          <span className="text-base font-normal text-muted">
                            {t(b.interval === 'year' ? 'billing.yearly' : 'billing.monthly')}:{' '}
                            {current ? priceText(current) : t('billing.loadingPrice')}
                          </span>
                        </>
                      )}
                    </p>
                  )}
                  {b.trialEnd && (
                    <p className="text-sm text-muted">
                      {t('billing.trialUntil', { date: new Date(b.trialEnd).toLocaleString(locale) })}
                    </p>
                  )}
                  {b.paidThrough && (
                    <p className="text-sm text-muted">
                      {t('billing.paidUntil', { date: new Date(b.paidThrough).toLocaleString(locale) })}
                    </p>
                  )}
                  <p className="text-sm text-muted">
                    {b.cancelAtPeriodEnd ? t('billing.cancelled') : !choosing && t('billing.terms')}
                  </p>
                </div>
                {b.limits && b.usage && (
                  <Usage limits={b.limits} usage={b.usage} used={b.companiesUsed} locale={locale} />
                )}
                {b.canManage && (
                  <div className="flex flex-wrap items-center gap-3">
                    {b.hostedInvoiceUrl && (
                      <a className="text-sm text-accent" href={b.hostedInvoiceUrl}>
                        {t('billing.finishPayment')} →
                      </a>
                    )}
                    {b.status !== 'pending' && (
                      <Button disabled={busy} onClick={() => void go()}>
                        {t('billing.manage')}
                      </Button>
                    )}
                  </div>
                )}
              </section>
            )}

            {b && !b.canManage && !b.exempt && (
              <p className="mx-auto max-w-3xl text-center text-sm text-muted">
                {t(me.data?.role === 'owner' ? 'billing.managedByOwner' : 'billing.askOwner')}
              </p>
            )}

            {choosing && plans.data && (
              <PlanPicker
                plans={plans.data.plans}
                taxMode={plans.data.taxMode}
                suggested={suggested}
                busy={busy}
                locale={locale}
                onChoose={(choice) => void go(choice)}
              />
            )}
          </>
        )}

        <footer className="flex flex-col items-center gap-3 text-sm">
          {b?.hasAccess && !successView && (
            <a href="/onboarding" className="text-accent">
              {t('billing.continue')} →
            </a>
          )}
          {b?.canManage && !successView && (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={async () => {
                setError(null);
                setBusy(true);
                try {
                  const r = await authClient.sendVerificationEmail({
                    email: me.data!.user.email,
                    callbackURL: '/billing',
                  });
                  if (r.error) throw new Error(r.error.message);
                  setMessage(t('billing.verificationSent'));
                } catch (e) {
                  setError(e);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t('billing.resendVerification')}
            </Button>
          )}
          {message && <p role="status">{message}</p>}
        </footer>
      </div>
    </div>
  );
}

function Usage({
  limits,
  usage,
  used,
  locale,
}: {
  limits: PlanLimits;
  usage: NonNullable<BillingDto['usage']>;
  used: number;
  locale: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 border-t border-border pt-4">
      <p className="text-xs font-medium text-muted">
        {t('billing.usagePeriod', { date: new Date(usage.periodEnd).toLocaleDateString(locale) })}
      </p>
      <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
        <Meter
          label={t('billing.meter.runs')}
          used={usage.runsPerMonth}
          limit={limits.runsPerMonth}
          locale={locale}
        />
        <Meter
          label={t('billing.meter.sandbox')}
          used={usage.sandboxHours}
          limit={limits.sandboxHoursPerMonth}
          locale={locale}
        />
        <Meter label={t('billing.meter.agents')} used={usage.agents} limit={limits.agents} locale={locale} />
        <Meter label={t('billing.meter.seats')} used={usage.seats} limit={limits.seats} locale={locale} />
        <Meter label={t('billing.meter.companies')} used={used} limit={limits.companies} locale={locale} />
      </div>
    </div>
  );
}
